import { useEffect, useState } from 'react';
import { useSessionContext } from "./Session";
import { whenOpenReplayStarted } from './openReplay';
import log from 'loglevel';


// Tags the OpenReplay recording with the logged-in user. The tracker itself is started
// outside React (openReplay.js); this only needs the session, so it is the one part that
// lives in App.
//
// The tracker is held in state so that the identify effect re-runs as soon as
// tracker.start() resolves. This avoids the race where a user logs in before start() is
// finished — without the state trigger, the identify effect would have already fired with
// `tracker` still null and never re-fire.
const OpenReplayIdentify = () => {
  const [tracker, setTracker] = useState(null);
  const { sessionContext } = useSessionContext();
  log.debug(`OpenReplayIdentify: sessionContext`, sessionContext);
  const { isAdminUser, session } = sessionContext || {};
  const { user } = session || {};
  const { email, name } = user || {};

  useEffect(() => {
    let mounted = true;
    whenOpenReplayStarted().then((started) => {
      if (mounted && started) {
        setTracker(started);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Identify (or de-identify) the user when the session context changes
  // OR when the tracker becomes available after start() resolves.
  //
  // Uses the recommended tracker.identify() / tracker.analytics.identify()
  // API instead of the older setUserID(). identify() persists the user ID
  // to sessionStorage, creates proper identity events in the analytics
  // batch, and automatically resets properties when the user ID changes.
  useEffect(() => {
    if (!tracker) {
      return;
    }
    if (email) {
      log.info(`OpenReplay.identify(${email})`);
      tracker.identify(email);
      tracker.setMetadata("name", name || "");
      tracker.setMetadata("isAdminUser", String(Boolean(isAdminUser)));
    } else {
      // Logged out — clear the user identity. tracker.identify() uses
      // the people/analytics system which has a proper reset() that
      // clears user_id and own properties. We still manually clear
      // metadata keys set via setMetadata() since those live on the
      // core tracker, not the people system.
      log.debug("OpenReplay: clearing user identity and metadata");
      tracker.setUserID("");
      tracker.setMetadata("name", "");
      tracker.setMetadata("isAdminUser", "");
    }
  }, [email, name, isAdminUser, tracker]);

  return null;
};

export {
  OpenReplayIdentify
};
